import { PropsWithChildren, ReactNode } from 'react';
import { BoxProps } from '@mui/material';
import { FormPage } from '/src/components';
import { Note } from '/src/lib/models';
import { CRUDMode, DataModelType } from '/src/lib/types';

export type CreatePageProps = PropsWithChildren & {
  /**
   * Data model to create
   */
  modelType: DataModelType;
  /**
   * Creates a new instance of the selected data model
   * @returns void | Promise<void>
   */
  onCreate: () => void | Promise<void>;
  /**
   * Optional sidebar component to display
   */
  sidebar?: ReactNode;
  /**
   * Element ID
   */
  id?: string;
  /**
   * Sets the verbiage when talking about creating
   */
  createVerb?: string; // defaults to "Add"
  /**
   * Icon representing the data model to be created
   */
  createIcon?: React.ReactNode;
  /**
   * Label for page's submit button
   */
  submitLabel?: string;
  /**
   * Display sidebar items
   */
  sidebarItems?: boolean[];
  /**
   * Keeps track of which sidebar items are open
   * @param items Sidebar items
   * @returns void
   */
  setSidebarItems?: (items: boolean[]) => void;
  /**
   * Flex value for sidebar
   */
  sidebarFlex?: string;
  /**
   * Flex value for page content
   */
  contentFlex?: string;
  /**
   * Page element width
   */
  width?: BoxProps['width'];
  /**
   * Config options for the Notes component
   */
  noteOptions?: {
    notes: Note[];
    onChange: (notes: Note[]) => void;
  };
  /**
   * Config options for the Documents component
   */
  documentOptions?: {
    files: string[];
    onChange: (files: string[]) => void;
  };
  /**
   * Flex value for Notes and Documents components
   */
  notesDocsFlex?: string;
};

/**
 * Create Page Layout
 */
export const CreatePage = ({
  children,
  modelType,
  onCreate,
  id,
  createVerb = 'Add',
  createIcon,
  ...props
}: CreatePageProps) => {
  return (
    <FormPage
      modelType={modelType}
      mode={CRUDMode.Create}
      verb={createVerb}
      icon={createIcon}
      onSubmit={onCreate}
      id={id}
      {...props}
    >
      {children}
    </FormPage>
  );
};
